import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Activity, Search } from "lucide-react";
import { format } from "date-fns";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

export const Route = createFileRoute("/_authenticated/admin/logs")({
  component: ActivityLogsPage,
});

interface LogEntry {
  id: string;
  action: string;
  details: string | null;
  created_at: string;
  user_id: string | null;
  profiles?: { full_name: string | null } | null;
}

function ActivityLogsPage() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    async function fetchLogs() {
      setLoading(true);
      let query = (supabase as any)
        .from("activity_logs")
        .select(
          `
          id,
          action,
          details,
          created_at,
          user_id,
          profiles!activity_logs_user_id_fkey(full_name)
        `,
        )
        .order("created_at", { ascending: false })
        .limit(100);

      if (searchTerm) {
        query = query.or(`action.ilike.%${searchTerm}%,details.ilike.%${searchTerm}%`);
      }
      if (actionFilter !== "all") {
        query = query.ilike("action", `%${actionFilter}%`);
      }
      if (startDate) {
        query = query.gte("created_at", new Date(startDate).toISOString());
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        query = query.lte("created_at", end.toISOString());
      }

      const { data } = await query;

      setLogs((data as any) || []);
      setLoading(false);
    }

    // Debounce the fetch
    const timeoutId = setTimeout(() => {
      fetchLogs();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, actionFilter, startDate, endDate]);

  return (
    <div>
      <div className="flex flex-col gap-4 mb-6">
        <h1 className="text-3xl font-extrabold flex items-center gap-3">
          <Activity className="h-8 w-8 text-primary" /> Activity Logs
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 bg-muted/30 p-4 rounded-2xl">
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="البحث في التفاصيل..."
              className="pl-9 bg-background"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="bg-background">
              <SelectValue placeholder="نوع النشاط" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">الكل (All Actions)</SelectItem>
              <SelectItem value="USER">المستخدمين (Users)</SelectItem>
              <SelectItem value="ROLE">الصلاحيات (Roles)</SelectItem>
              <SelectItem value="GRADE">التقييم (Grades)</SelectItem>
            </SelectContent>
          </Select>

          <Input
            type="date"
            placeholder="من تاريخ"
            className="bg-background"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            type="date"
            placeholder="إلى تاريخ"
            className="bg-background"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">No logs found.</div>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => (
            <Card key={log.id} className="rounded-2xl border-0 shadow-soft">
              <CardContent className="p-4 flex items-start gap-4">
                <Avatar className="h-10 w-10 border-2 border-primary/20 shrink-0 mt-1">
                  <AvatarFallback className="bg-primary/10 text-primary font-bold">
                    {log.profiles?.full_name
                      ? log.profiles.full_name.substring(0, 2).toUpperCase()
                      : "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start gap-2 flex-wrap">
                    <div>
                      <h3 className="font-bold text-base">{log.action}</h3>
                      <p className="text-xs text-muted-foreground">
                        بواسطة:{" "}
                        <span className="font-medium text-foreground">
                          {log.profiles?.full_name || log.user_id || "Unknown"}
                        </span>
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full whitespace-nowrap">
                      {format(new Date(log.created_at), "MMM d, yyyy h:mm a")}
                    </span>
                  </div>
                  <div className="text-sm mt-2 p-3 bg-muted/40 rounded-xl whitespace-pre-wrap font-mono text-xs overflow-x-auto">
                    {typeof log.details === "object" && log.details !== null
                      ? JSON.stringify(log.details, null, 2)
                      : log.details}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
